<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const platformCodes = [
  { name: 'GoFood', code: 'gofood' },
  { name: 'GrabFood', code: 'grabfood' },
  { name: 'ShopeeFood', code: 'shopeefood' },
  { name: 'Lainnya', code: 'other' },
]

const platformCodesHtml = platformCodes
  .map(p => `<span class="userguide-chip">${p.name} → <code>${p.code}</code></span>`)
  .join(' ')

const sections = [
  {
    title: '🛵 Apa itu Platform Delivery?',
    icon: 'ri-motorbike-line',
    color: 'warning',
    content: `
      <p class="mb-4">
        Kalau toko Anda menerima pesanan lewat aplikasi ojek online (GoFood,
        GrabFood, ShopeeFood, dll), Anda perlu daftarkan dulu platform-platform
        itu di sistem. Tujuannya supaya setiap pesanan delivery bisa ditandai
        "masuk dari platform mana" — berguna untuk rekap dan laporan nanti.
      </p>
      <VAlert type="info" variant="tonal">
        Anggap ini seperti daftar "rekanan ojek online" yang bekerja sama
        dengan toko Anda. Cukup daftarkan sekali, dan platform itu bisa
        dipilih berulang kali oleh kasir setiap ada pesanan delivery.
      </VAlert>
    `,
  },
  {
    title: '➕ Cara Menambah Platform Delivery',
    icon: 'ri-add-circle-line',
    color: 'success',
    content: `
      <ol class="mb-4">
        <li class="mb-2">
          Buka menu <strong>Ekstensi F&amp;B → Tipe Order &amp; Delivery →
          Platform Delivery</strong>
        </li>
        <li class="mb-2">Klik tombol <strong>"+ Tambah Platform"</strong></li>
        <li class="mb-2">Isi form berikut</li>
        <li class="mb-0">Klik <strong>"Simpan"</strong></li>
      </ol>

      <table class="userguide-table mb-4">
        <thead>
          <tr><th>Field</th><th>Wajib?</th><th>Penjelasan</th><th>Contoh</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Nama Platform</strong></td>
            <td>✅ Ya</td>
            <td>Nama yang akan tampil di dropdown pilihan kasir</td>
            <td>GoFood</td>
          </tr>
          <tr>
            <td><strong>Kode Platform</strong></td>
            <td>✅ Ya</td>
            <td>Kategori baku sesuai jenis platform-nya</td>
            <td>gofood</td>
          </tr>
          <tr>
            <td><strong>Komisi Platform (%)</strong></td>
            <td>❌ Tidak</td>
            <td>Persentase komisi yang biasanya dipotong platform tersebut — untuk catatan/referensi internal Anda</td>
            <td>20</td>
          </tr>
          <tr>
            <td><strong>Aktif</strong></td>
            <td>✅ Ya (bawaan: aktif)</td>
            <td>Kalau dimatikan, platform tidak muncul lagi di pilihan kasir</td>
            <td>Aktif</td>
          </tr>
        </tbody>
      </table>

      <p class="mb-2 text-body-2"><strong>Pilihan Kode Platform yang tersedia:</strong></p>
      <div class="mb-4">
        ${platformCodesHtml}
      </div>

      <VAlert type="warning" variant="tonal">
        ⚠️ Kalau platform yang Anda pakai tidak ada di daftar kode (mis. platform
        lokal khusus daerah Anda), pilih <strong>"Lainnya"</strong> dan gunakan
        Nama Platform untuk membedakannya.
      </VAlert>
    `,
  },
  {
    title: '🧾 Cara Pakai Saat Transaksi',
    icon: 'ri-shopping-cart-2-line',
    color: 'primary',
    content: `
      <ol class="mb-4">
        <li class="mb-2">Di halaman kasir, pilih tombol tipe order <strong>"Delivery"</strong></li>
        <li class="mb-2">
          Akan muncul dropdown <strong>"Platform Delivery"</strong> — pilih salah
          satu platform yang sudah didaftarkan
        </li>
        <li class="mb-2">Lanjutkan input pesanan seperti biasa</li>
        <li class="mb-0">
          Tombol <strong>"Bayar"</strong> baru bisa diklik setelah platform dipilih
        </li>
      </ol>
      <VAlert type="warning" variant="tonal">
        ⚠️ Kalau dropdown platform kosong ("Belum ada platform aktif"), berarti
        Anda belum mendaftarkan satu pun platform, atau semuanya sedang
        dinonaktifkan. Tambahkan/aktifkan dulu minimal 1 platform.
      </VAlert>
    `,
  },
  {
    title: '✏️ Edit & Nonaktifkan Platform',
    icon: 'ri-edit-line',
    color: 'info',
    content: `
      <ol class="mb-4">
        <li class="mb-2">Klik ikon <strong>pensil (✏️)</strong> di baris platform yang ingin diubah</li>
        <li class="mb-2">Ubah field yang perlu (misalnya update persentase komisi)</li>
        <li class="mb-0">Klik <strong>"Update"</strong></li>
      </ol>
      <p class="mb-3">
        Untuk menghentikan sementara satu platform tanpa menghapus datanya,
        cukup edit platform tersebut dan matikan sakelar <strong>Aktif</strong>.
        Ini lebih aman daripada menghapus, karena riwayat pesanan lama dari
        platform itu tetap tersimpan rapi.
      </p>
      <VAlert type="error" variant="tonal">
        Tombol <strong>hapus (🗑️)</strong> akan menghapus data platform secara
        permanen. Kalau ragu, gunakan opsi nonaktifkan (edit → matikan Aktif)
        daripada menghapus.
      </VAlert>
    `,
  },
]
</script>

<template>
  <div>
    <RouterLink to="/userguide/fb-modern">
      <VBtn variant="text" prepend-icon="ri-arrow-left-line" class="mb-2">
        Kembali ke F&B Modern
      </VBtn>
    </RouterLink>

    <div class="d-flex align-center mb-4">
      <VAvatar color="warning" variant="tonal" size="64" rounded="lg" class="me-4">
        <VIcon icon="ri-motorbike-line" size="36" />
      </VAvatar>
      <div>
        <h4 class="text-h4 font-weight-bold mb-1">Modul 14: Platform Delivery</h4>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Daftarkan GoFood, GrabFood, ShopeeFood, atau platform ojek online lainnya
        </p>
      </div>
    </div>

    <VExpansionPanels variant="accordion" multiple>
      <VExpansionPanel v-for="(section, index) in sections" :key="index">
        <VExpansionPanelTitle>
          <VIcon :icon="section.icon" :color="section.color" class="me-3" />
          {{ section.title }}
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div v-html="section.content" />
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- FAQ -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
    <VExpansionPanels variant="accordion">
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Apakah komisi platform otomatis dipotong dari penjualan saya?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Field "Komisi Platform" saat ini berfungsi sebagai catatan/referensi
          untuk Anda — belum otomatis memotong angka penjualan yang tercatat
          di sistem. Untuk kebutuhan rekonsiliasi komisi yang presisi, tetap
          cocokkan dengan laporan resmi dari aplikasi platform terkait.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Bisa tidak saya daftarkan 2 platform dengan kode yang sama (misalnya 2 akun GoFood)?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Kode platform harus unik per toko. Kalau Anda punya 2 outlet GoFood
          berbeda, gunakan Nama Platform yang berbeda (mis. "GoFood Cabang 1"
          dan "GoFood Cabang 2") dengan kode "Lainnya" untuk salah satunya, atau
          tanyakan ke tim support untuk kebutuhan spesifik ini.
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- Related -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">🔗 Halaman Terkait</h5>
    <VRow>
      <VCol
        v-for="link in [
          { title: 'Tipe Order & Biaya Layanan', sub: 'Atur biaya tambahan untuk pesanan delivery', icon: 'ri-percent-line', color: 'success', to: { name: 'userguide-fb-modern-order-type' } },
        ]"
        :key="link.title"
        cols="12"
        md="6"
      >
        <RouterLink :to="link.to" class="text-decoration-none">
          <VCard variant="outlined" hover>
            <VCardText class="d-flex align-center gap-3 py-3">
              <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                <VIcon :icon="link.icon" size="22" />
              </VAvatar>
              <div>
                <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
              </div>
            </VCardText>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.userguide-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.userguide-table th,
.userguide-table td {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}

.userguide-table th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  font-weight: 600;
}

.userguide-chip {
  display: inline-block;
  background: rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 6px;
  padding: 4px 10px;
  margin: 2px;
  font-size: 0.8125rem;
}
</style>
